﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { api, type ResolutionRow } from "../app/api";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { StartResolutionModal } from "../components/common/StartResolutionModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose } from "../hooks/useActionMenuAutoClose";

type Stimmrechtsart = "kopf" | "stimmrecht" | "mea";
type QuorumArt = "einfache_mehrheit" | "qualifizierte_mehrheit" | "allstimmig";
type TriggerNotificationChannel = "email" | "facilioo";
type ResolutionStatusFilter = "" | "entwurf" | "aktiv" | "abgeschlossen";
type ResolutionResultFilter = "" | "angenommen" | "abgelehnt" | "offen";
type ResolutionSortField = "beschlussnummer" | "objektnummer" | "objektname" | "titel" | "status" | "restlaufzeit" | "ruecklaufquote";

type ResolutionForm = {
  objectId: string;
  beschlussnummer: string;
  titel: string;
  startdatum: string;
  endedatum: string;
  stimmrechtsart: Stimmrechtsart;
  quorum: QuorumArt;
  vorbeschluss_top: string;
  vorbeschluss_wev_datum: string;
  beschlusstext: string;
  facilioo_vorgangsnummer: string;
  dispatch_automation_enabled: boolean;
  trigger_notifications_enabled: boolean;
  trigger_notifications_channel: TriggerNotificationChannel;
  trigger_notifications_events: Record<string, boolean>;
  stimmberechtigte_einheiten: string[];
};

const TRIGGER_NOTIFICATION_EVENT_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "resolution_started", label: "Info über Start des Beschlusses" },
  { key: "invitation_sent_success", label: "Info über erfolgreichen Versand der Einladung" },
  { key: "email_bounce_after_retries", label: "Info über E-Mail Rückläufer nach fehlgeschlagenen Retries" },
  { key: "analog_postal_action_required", label: "Hinweis bei analogen Eigentümern (postalischer Versand erforderlich)" },
  { key: "vote_with_comment", label: "Info bei Stimmabgabe mit Kommentar (inkl. Kommentarinhalt)" },
  { key: "reminder_sent", label: "Info über Versand von Erinnerungsschreiben (1/2) inkl. Empfänger" },
  { key: "each_vote_submitted", label: "Info über jede Stimmabgabe" },
  { key: "preliminary_result_locked", label: "Info bei vorläufigem Endergebnis (Ergebnis kann nicht mehr kippen)" },
  { key: "resolution_finished_with_result", label: "Info über Ende des Beschlusses inkl. Ergebnis" }
];

const defaultTriggerNotificationEvents = () =>
  Object.fromEntries(TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => [eventOption.key, false])) as Record<string, boolean>;

const allTriggerNotificationEvents = () =>
  Object.fromEntries(TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => [eventOption.key, true])) as Record<string, boolean>;

const ResolutionMenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7.1 4.6h6.6l3.2 3.2v11.6H7.1z" stroke="currentColor" strokeWidth="1.85" strokeLinejoin="round" />
    <path d="M13.7 4.8v3.1h3" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
    <path d="m9.4 14.1 2 2 4.2-5" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="17.7" cy="17.8" r="2.1" fill="#fff" stroke="currentColor" strokeWidth="1.85" />
  </svg>
);

const ResolutionCreateSectionIcon = ({ name }: { name: "resolution" | "form" | "upload" | "search" | "select" | "unit" | "pin" | "home" | "owner" }) => {
  if (name === "resolution") {
    return <ResolutionMenuIcon />;
  }
  if (name === "upload") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4.3 14.8h11.4a1.5 1.5 0 0 0 1.5-1.5V6.8a1.5 1.5 0 0 0-1.5-1.5H10L8.5 3.8H4.3a1.5 1.5 0 0 0-1.5 1.5v8a1.5 1.5 0 0 0 1.5 1.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        <path d="M10 12.2V7.5M7.9 9.4 10 7.3l2.1 2.1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "search") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="8.9" cy="8.9" r="5.6" stroke="currentColor" strokeWidth="1.8" />
        <path d="m13 13 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "select") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="4.1" y="4.1" width="11.8" height="11.8" rx="2.1" stroke="currentColor" strokeWidth="1.7" />
        <path d="m7.2 10.1 2 2 3.9-4.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "unit") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <rect x="5.1" y="4.1" width="9.8" height="11.8" rx="1.8" stroke="currentColor" strokeWidth="1.7" />
        <path d="M7.4 7.5h2M10.8 7.5h2M7.4 10.5h2M10.8 10.5h2M8.1 15.9v-2.2h3.8v2.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (name === "pin") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M10 17s4.8-4.6 4.8-8.1a4.8 4.8 0 1 0-9.6 0C5.2 12.4 10 17 10 17Z" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="10" cy="8.9" r="1.6" stroke="currentColor" strokeWidth="1.7" />
      </svg>
    );
  }
  if (name === "home") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M4.4 8.7 10 4.4l5.6 4.3v6.8h-4.1v-3.8h-3v3.8H4.4V8.7Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      </svg>
    );
  }
  if (name === "owner") {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M6.8 9.4h6.1M6.8 12.2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
};

const ResolutionCreateTableHeader = ({
  label,
  icon,
  center = false
}: {
  label: string;
  icon: Parameters<typeof ResolutionCreateSectionIcon>[0]["name"];
  center?: boolean;
}) => (
  <span className={`resolutions-v2-th-plain${center ? " resolution-create-v2-th-center" : ""}`}>
    <span className="resolutions-v2-th-icon" aria-hidden="true">
      <ResolutionCreateSectionIcon name={icon} />
    </span>
    <span className="resolutions-v2-th-label">{label}</span>
  </span>
);

const mergeSelectedFiles = (previous: File[], incoming: File[], replace = false) => {
  const base = replace ? [] : previous;
  const deduped = new Map<string, File>();
  for (const file of base) {
    deduped.set(`${file.name}__${file.size}__${file.lastModified}`, file);
  }
  for (const file of incoming) {
    deduped.set(`${file.name}__${file.size}__${file.lastModified}`, file);
  }
  return Array.from(deduped.values());
};

const normalizeTriggerNotificationEvents = (value?: Record<string, boolean>) => {
  const base = defaultTriggerNotificationEvents();
  if (!value) return base;
  for (const [key, raw] of Object.entries(value)) {
    if (!(key in base)) continue;
    base[key] = Boolean(raw);
  }
  return base;
};

const getDefaultTriggerNotificationChannel = (faciliooEnabled: boolean): TriggerNotificationChannel =>
  faciliooEnabled ? "facilioo" : "email";

type DeleteConfirmState = {
  id: string;
  beschlussnummer: string;
  code: string;
  input: string[];
  error: string;
} | null;
type StopConfirmState = {
  id: string;
  beschlussnummer: string;
  code: string;
  input: string[];
  error: string;
} | null;


type StartConfirmState = {
  id: string;
  beschlussnummer: string;
  endedatum: string;
  error: string;
} | null;

type UnitSelectionRow = {
  unitId: string;
  einheitsnummer: string;
  bezeichnung: string;
  einheitstyp: string;
  aufgang: string;
  eigentuemerInfo: string;
};

const buildEmptyResolutionForm = (faciliooEnabled: boolean): ResolutionForm => ({
  objectId: "",
  beschlussnummer: "",
  titel: "",
  startdatum: "",
  endedatum: "",
  stimmrechtsart: "kopf",
  quorum: "allstimmig",
  vorbeschluss_top: "",
  vorbeschluss_wev_datum: "",
  beschlusstext: "",
  facilioo_vorgangsnummer: "",
  dispatch_automation_enabled: false,
  trigger_notifications_enabled: false,
  trigger_notifications_channel: getDefaultTriggerNotificationChannel(faciliooEnabled),
  trigger_notifications_events: defaultTriggerNotificationEvents(),
  stimmberechtigte_einheiten: []
});

const isVorbeschlussRequired = (quorum: QuorumArt) => quorum !== "allstimmig";

const getTodayDateKey = () => new Date().toISOString().slice(0, 10);

const getDateFieldErrors = (startdatum?: string, endedatum?: string): { startError: string | null; endError: string | null } => {
  const today = getTodayDateKey();
  const startError = startdatum && startdatum <= today ? "Startdatum muss in der Zukunft liegen" : null;
  const endError = startdatum && endedatum && endedatum <= startdatum ? "Enddatum muss nach dem Startdatum liegen" : null;
  return { startError, endError };
};

const formatDateDE = (value?: string) => {
  if (!value) return "-";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

const getRemainingDays = (endDate?: string) => {
  if (!endDate) return null;
  const match = endDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const end = new Date(Number(yearRaw), Number(monthRaw) - 1, Number(dayRaw));
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor((end.getTime() - today.getTime()) / millisecondsPerDay) + 1;
  return Math.max(0, diffDays);
};
const parseFacilioo = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (!/^\d{1,10}$/.test(trimmed)) throw new Error("Facilioo Vorgangsnummer muss 1 bis 10-stellig numerisch sein");
  return Number(trimmed);
};

const formatStatus = (status: ResolutionRow["status"]) => {
  if (status === "aktiv") return "läuft";
  if (status === "abgeschlossen") return "beendet";
  return "Entwurf";
};

const formatStimmrechtsart = (value: Stimmrechtsart) => {
  if (value === "kopf") return "Kopfstimmen";
  if (value === "mea") return "MEA";
  return "Stimmrecht";
};

const formatQuorum = (value: QuorumArt) => {
  if (value === "qualifizierte_mehrheit") return "Qualifizierte Mehrheit";
  if (value === "allstimmig") return "Allstimmig";
  return "Einfache Mehrheit";
};

const formatSelectionUnitNumber = (value: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  return /^\d+$/.test(raw) ? raw.padStart(4, "0") : raw;
};

const formatOwnerDisplayName = (owner: Awaited<ReturnType<typeof api.owners>>["data"][number]) => {
  const parts = [
    String(owner.anrede ?? "").trim(),
    String(owner.vorname ?? "").trim(),
    String(owner.name_1 ?? "").trim(),
    String(owner.name_2 ?? "").trim(),
    String(owner.name_3 ?? "").trim()
  ].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return String(owner.vollname ?? "").trim() || "-";
};

const formatOwnerLabelFallback = (value?: string) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  return raw.replace(/^\s*\d+\s*-\s*/u, "").trim() || raw;
};

const normalizeValidationErrorMessage = (error: unknown) => {
  const fallback = "Facilioo Vorgang konnte nicht validiert werden.";
  const raw = error instanceof Error ? error.message : fallback;
  return String(raw).replace(/^API\s+\d+\s*:?\s*/i, "").trim() || fallback;
};

const getOutcome = (r: ResolutionRow): { label: string; tone?: "success" | "danger" } => {
  if (r.status !== "abgeschlossen") return { label: "-" };
  return r.quorum_erfuellt ? { label: "Angenommen", tone: "success" } : { label: "Abgelehnt", tone: "danger" };
};

const buildUnitSelectionRows = (
  units: Awaited<ReturnType<typeof api.units>>["data"],
  assignments: Awaited<ReturnType<typeof api.assignments>>["data"],
  owners: Awaited<ReturnType<typeof api.owners>>["data"]
): UnitSelectionRow[] => {
  const ownerMap = new Map(owners.map((o) => [o.id, formatOwnerDisplayName(o)]));
  return units
    .filter((u) => u.status === "aktiv")
    .map((unit) => {
      const ownerLabels = assignments
        .filter((a) => a.unitId === unit.id && a.status === "aktiv")
        .map((a) => ownerMap.get(a.ownerId) ?? formatOwnerLabelFallback(a.eigentuemername))
        .filter(Boolean);
      return {
        unitId: unit.id,
        einheitsnummer: unit.einheitsnummer,
        bezeichnung: unit.bezeichnung || "-",
        einheitstyp: unit.einheitstyp || "-",
        aufgang: unit.aufgangName || "-",
        eigentuemerInfo: Array.from(new Set(ownerLabels)).join(" | ") || "-"
      };
    })
    .sort((a, b) => a.einheitsnummer.localeCompare(b.einheitsnummer));
};

const hasConfiguredStimmrechte = (units: Awaited<ReturnType<typeof api.units>>["data"]) =>
  units.some((unit) => Number(unit.stimmrecht ?? 0) > 0);

const resolveDefaultStimmrechtsart = (objectDefault: Stimmrechtsart | undefined, hasStimmrechte: boolean): Stimmrechtsart => {
  if (objectDefault === "stimmrecht" && !hasStimmrechte) return "kopf";
  return objectDefault ?? (hasStimmrechte ? "stimmrecht" : "kopf");
};

const renderUnitSelector = (
  rows: UnitSelectionRow[],
  selectedUnitIds: string[],
  ownerSearch: string,
  setOwnerSearch: (value: string) => void,
  unitTypeFilter: string,
  setUnitTypeFilter: (value: string) => void,
  entranceFilter: string,
  setEntranceFilter: (value: string) => void,
  onToggleUnit: (unitId: string, checked: boolean) => void,
  onSelectAll: () => void,
  onDeselectAll: () => void,
  onMarkFiltered: () => void,
  onDemarkFiltered: () => void,
  isFrameworkV2 = false
) => {
  const needle = ownerSearch.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    const unitNumberFormatted = formatSelectionUnitNumber(row.einheitsnummer).toLowerCase();
    const searchOk =
      !needle ||
      row.eigentuemerInfo.toLowerCase().includes(needle) ||
      row.einheitsnummer.toLowerCase().includes(needle) ||
      unitNumberFormatted.includes(needle);
    const typeOk = !unitTypeFilter || row.einheitstyp === unitTypeFilter;
    const entranceOk = !entranceFilter || row.aufgang === entranceFilter;
    return searchOk && typeOk && entranceOk;
  });
  const availableUnitTypes = Array.from(new Set(rows.map((row) => row.einheitstyp))).sort((a, b) => a.localeCompare(b));
  const availableEntrances = Array.from(new Set(rows.map((row) => row.aufgang))).sort((a, b) => a.localeCompare(b));

  return (
    <div className="form-field form-field-wide resolution-selection-box">
      <div className={isFrameworkV2 ? "resolution-create-v2-section-head" : undefined}>
        {isFrameworkV2 ? (
          <span className="resolution-create-v2-section-icon" aria-hidden="true">
            <ResolutionCreateSectionIcon name="select" />
          </span>
        ) : null}
        <label>Stimmberechtigte Eigentümer/Einheiten*</label>
      </div>
      <div className={isFrameworkV2 ? "resolutions-v2-filterbar resolution-create-v2-unit-filterbar" : undefined} style={isFrameworkV2 ? undefined : { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <div className={isFrameworkV2 ? "resolutions-v2-filter-fields resolution-create-v2-filter-fields" : undefined}>
          <label className={isFrameworkV2 ? "resolutions-v2-filter-input resolution-create-v2-filter-input" : undefined}>
            {isFrameworkV2 ? (
              <span className="resolutions-v2-filter-icon" aria-hidden="true">
                <ResolutionCreateSectionIcon name="search" />
              </span>
            ) : null}
            <input placeholder="Eigentümer suchen (ID oder Name)" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} style={isFrameworkV2 ? undefined : { minWidth: 280 }} />
          </label>
        <select value={unitTypeFilter} onChange={(e) => setUnitTypeFilter(e.target.value)} style={isFrameworkV2 ? undefined : { minWidth: 180 }}>
          <option value="">Alle Einheitstypen</option>
          {availableUnitTypes.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
        <select value={entranceFilter} onChange={(e) => setEntranceFilter(e.target.value)} style={isFrameworkV2 ? undefined : { minWidth: 220 }}>
          <option value="">Alle Aufgänge</option>
          {availableEntrances.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
        </div>
        <div className={isFrameworkV2 ? "resolution-create-v2-filter-actions" : undefined}>
        <button type="button" onClick={onSelectAll}>Alle markieren</button>
        <button type="button" onClick={onDeselectAll}>Alle demarkieren</button>
        <button type="button" onClick={onMarkFiltered} disabled={!filteredRows.length}>Gefilterte markieren</button>
        <button type="button" onClick={onDemarkFiltered} disabled={!filteredRows.length}>Gefilterte demarkieren</button>
        </div>
      </div>
      <div className={isFrameworkV2 ? "resolution-create-v2-table-wrap" : undefined} style={isFrameworkV2 ? undefined : { border: "1px solid var(--line)", borderRadius: 6, minHeight: 220, height: 280, resize: "vertical", overflow: "auto" }}>
        <table className={isFrameworkV2 ? "resolution-create-v2-unit-table" : undefined}>
          <thead>
            <tr>
              <th style={{ width: 60 }}>{isFrameworkV2 ? <ResolutionCreateTableHeader label="Aktiv" icon="select" center /> : "Aktiv"}</th>
              <th style={{ textAlign: "center" }}>{isFrameworkV2 ? <ResolutionCreateTableHeader label="Einheit" icon="unit" center /> : "Einheit"}</th>
              <th>{isFrameworkV2 ? <ResolutionCreateTableHeader label="Aufgang" icon="home" /> : "Aufgang"}</th>
              <th>{isFrameworkV2 ? <ResolutionCreateTableHeader label="Lage" icon="pin" /> : "Lage"}</th>
              <th>{isFrameworkV2 ? <ResolutionCreateTableHeader label="Einheitstyp" icon="form" /> : "Einheitstyp"}</th>
              <th>{isFrameworkV2 ? <ResolutionCreateTableHeader label="Eigentümer" icon="owner" /> : "Eigentümer"}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.unitId}>
                <td><input type="checkbox" checked={selectedUnitIds.includes(row.unitId)} onChange={(e) => onToggleUnit(row.unitId, e.target.checked)} /></td>
                <td style={{ textAlign: "center" }}>{formatSelectionUnitNumber(row.einheitsnummer)}</td>
                <td>{row.aufgang}</td>
                <td>{row.bezeichnung}</td>
                <td>{row.einheitstyp}</td>
                <td>{row.eigentuemerInfo}</td>
              </tr>
            ))}
            {!filteredRows.length ? <tr><td colSpan={6}>Keine passenden Einheiten gefunden.</td></tr> : null}
          </tbody>
        </table>
      </div>
      <div className={isFrameworkV2 ? "resolution-create-v2-selection-count" : undefined} style={isFrameworkV2 ? undefined : { marginTop: 6, color: "var(--text-soft)", fontSize: 12 }}>Ausgewählt: {selectedUnitIds.length} von {rows.length} Einheiten</div>
    </div>
  );
};

type ResolutionsPageProps = {
  canManageMasterDataActions?: boolean;
  canCreateResolutions?: boolean;
  canEditResolutions?: boolean;
  canDeleteResolutions?: boolean;
  canDeleteCompletedResolutions?: boolean;
};

export const ResolutionsPage = ({
  canManageMasterDataActions = false,
  canCreateResolutions = false,
  canEditResolutions = false,
  canDeleteResolutions = false,
  canDeleteCompletedResolutions = false
}: ResolutionsPageProps) => {
  const canDeleteAnyResolution = canDeleteResolutions || canDeleteCompletedResolutions;
  const qc = useQueryClient();
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ResolutionStatusFilter>("");
  const [resultFilter, setResultFilter] = useState<ResolutionResultFilter>("");
  const [sortBy, setSortBy] = useState<ResolutionSortField>("objektnummer");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);
  const [startConfirm, setStartConfirm] = useState<StartConfirmState>(null);
  const [stopConfirm, setStopConfirm] = useState<StopConfirmState>(null);
  const [editForm, setEditForm] = useState<(ResolutionForm & { id: string }) | null>(null);
  const [createOwnerSearch, setCreateOwnerSearch] = useState("");
  const [createUnitTypeFilter, setCreateUnitTypeFilter] = useState("");
  const [createEntranceFilter, setCreateEntranceFilter] = useState("");
  const [createDocuments, setCreateDocuments] = useState<File[]>([]);
  const [createDocumentsDropActive, setCreateDocumentsDropActive] = useState(false);
  const createDocumentsInputRef = useRef<HTMLInputElement | null>(null);
  const [createFaciliooValidationPending, setCreateFaciliooValidationPending] = useState(false);
  const [createFaciliooValidationError, setCreateFaciliooValidationError] = useState("");
  const [createFaciliooProcessTitle, setCreateFaciliooProcessTitle] = useState("");
  const [editOwnerSearch, setEditOwnerSearch] = useState("");
  const [editUnitTypeFilter, setEditUnitTypeFilter] = useState("");
  const [editEntranceFilter, setEditEntranceFilter] = useState("");
  const [editFaciliooValidationPending, setEditFaciliooValidationPending] = useState(false);
  const [editFaciliooValidationError, setEditFaciliooValidationError] = useState("");
  const [editFaciliooProcessTitle, setEditFaciliooProcessTitle] = useState("");
  const [pendingAutoSelectObjectId, setPendingAutoSelectObjectId] = useState<string | null>(null);

  const [form, setForm] = useState<ResolutionForm>(buildEmptyResolutionForm(false));

  const mergeCreateDocuments = (incoming: File[], replace = false) => {
    setCreateDocuments((prev) => mergeSelectedFiles(prev, incoming, replace));
  };

  const onCreateDocumentsDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setCreateDocumentsDropActive(false);
    mergeCreateDocuments(Array.from(event.dataTransfer.files ?? []), false);
  };

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), 450);
    return () => window.clearTimeout(handle);
  }, [search]);

  const { data, isError } = useQuery({
    queryKey: ["resolutions", page, pageSize, debouncedSearch, statusFilter, resultFilter, sortBy, sortDir],
    queryFn: () =>
      api.resolutions({
        page,
        pageSize,
        search: debouncedSearch,
        status: statusFilter || undefined,
        ergebnis: resultFilter || undefined,
        sortBy: sortBy === "restlaufzeit" ? "objektnummer" : sortBy,
        sortDir
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 3 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnMount: false
  });
  const { data: objectsData } = useQuery({
    queryKey: ["objects-select"],
    queryFn: () => api.objects({ page: 1, pageSize: 200, status: "aktiv", sortBy: "objektnummer", sortDir: "asc" })
  });
  const { data: uiSettingsData } = useQuery({
    queryKey: ["ui-settings-resolutions"],
    queryFn: () => api.uiSettings()
  });
  const { data: nextResolutionNumberData } = useQuery({
    queryKey: ["resolutions-next-beschlussnummer"],
    queryFn: () => api.nextResolutionNumber(),
    staleTime: 15_000
  });
  const { data: createUnitsData, isFetched: createUnitsFetched } = useQuery({
    queryKey: ["resolution-create-units", form.objectId],
    queryFn: () => api.units({ page: 1, pageSize: 1200, objectId: form.objectId, status: "aktiv" }),
    enabled: showCreate && !!form.objectId,
    staleTime: 5 * 60_000,
    refetchOnMount: false
  });
  const { data: createAssignmentsData } = useQuery({
    queryKey: ["resolution-create-assignments", form.objectId],
    queryFn: () => api.assignments({ page: 1, pageSize: 2000, objectId: form.objectId, status: "aktiv" }),
    enabled: showCreate && !!form.objectId,
    staleTime: 5 * 60_000,
    refetchOnMount: false
  });
  const { data: createOwnersData } = useQuery({
    queryKey: ["resolution-create-owners", form.objectId],
    queryFn: () => api.owners({ page: 1, pageSize: 1200, objectId: form.objectId, status: "aktiv" }),
    enabled: showCreate && !!form.objectId,
    staleTime: 5 * 60_000,
    refetchOnMount: false
  });
  const { data: editUnitsData } = useQuery({
    queryKey: ["resolution-edit-units", editForm?.objectId],
    queryFn: () => api.units({ page: 1, pageSize: 1200, objectId: editForm!.objectId, status: "aktiv" }),
    enabled: !!editForm?.objectId,
    staleTime: 5 * 60_000,
    refetchOnMount: false
  });
  const { data: editAssignmentsData } = useQuery({
    queryKey: ["resolution-edit-assignments", editForm?.objectId],
    queryFn: () => api.assignments({ page: 1, pageSize: 2000, objectId: editForm!.objectId, status: "aktiv" }),
    enabled: !!editForm?.objectId,
    staleTime: 5 * 60_000,
    refetchOnMount: false
  });
  const { data: editOwnersData } = useQuery({
    queryKey: ["resolution-edit-owners", editForm?.objectId],
    queryFn: () => api.owners({ page: 1, pageSize: 1200, objectId: editForm!.objectId, status: "aktiv" }),
    enabled: !!editForm?.objectId,
    staleTime: 5 * 60_000,
    refetchOnMount: false
  });

  const createUnitRows = useMemo(() => buildUnitSelectionRows(createUnitsData?.data ?? [], createAssignmentsData?.data ?? [], createOwnersData?.data ?? []), [createUnitsData?.data, createAssignmentsData?.data, createOwnersData?.data]);
  const editUnitRows = useMemo(() => buildUnitSelectionRows(editUnitsData?.data ?? [], editAssignmentsData?.data ?? [], editOwnersData?.data ?? []), [editUnitsData?.data, editAssignmentsData?.data, editOwnersData?.data]);
  const createHasStimmrechte = useMemo(() => hasConfiguredStimmrechte(createUnitsData?.data ?? []), [createUnitsData?.data]);
  const editHasStimmrechte = useMemo(() => hasConfiguredStimmrechte(editUnitsData?.data ?? []), [editUnitsData?.data]);
  const createObjectStandardStimmrechtsart = useMemo(
    () => (objectsData?.data ?? []).find((o) => o.id === form.objectId)?.standard_stimmrechtsart,
    [objectsData?.data, form.objectId]
  );
  const editObjectStandardStimmrechtsart = useMemo(
    () => (objectsData?.data ?? []).find((o) => o.id === editForm?.objectId)?.standard_stimmrechtsart,
    [objectsData?.data, editForm?.objectId]
  );
  const createCanUseStimmrecht = createHasStimmrechte;
  const editCanUseStimmrecht = editHasStimmrechte;
  const faciliooEnabled = uiSettingsData?.management?.faciliooEnabled !== false;
  const createDateErrors = getDateFieldErrors(form.startdatum, form.endedatum);
  const editDateErrors = getDateFieldErrors(editForm?.startdatum, editForm?.endedatum);
  const objectById = useMemo(() => {
    const map = new Map<string, { objektnummer: string; objektname: string }>();
    for (const objectRow of objectsData?.data ?? []) {
      map.set(objectRow.id, { objektnummer: objectRow.objektnummer, objektname: objectRow.objektname });
    }
    return map;
  }, [objectsData?.data]);

  const getFilteredResolutionUnitIds = (
    rows: UnitSelectionRow[],
    ownerSearch: string,
    unitTypeFilter: string,
    entranceFilter: string
  ) => {
    const needle = ownerSearch.trim().toLowerCase();
    return rows
      .filter((row) => {
        const searchOk = !needle || row.eigentuemerInfo.toLowerCase().includes(needle) || row.einheitsnummer.toLowerCase().includes(needle);
        const typeOk = !unitTypeFilter || row.einheitstyp === unitTypeFilter;
        const entranceOk = !entranceFilter || row.aufgang === entranceFilter;
        return searchOk && typeOk && entranceOk;
      })
      .map((row) => row.unitId);
  };

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, resultFilter, sortBy, sortDir, pageSize]);

  useEffect(() => {
    setOpenActionMenuId(null);
  }, [page, search, statusFilter, resultFilter, sortBy, sortDir, showCreate]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = () => setOpenActionMenuId(null);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) {
      setActionMenuPosition(null);
      setIsActionMenuHovered(false);
    }
  }, [openActionMenuId]);

  const openActionMenuAtTrigger = (menuId: string, trigger: HTMLElement, menuHeight = 188) => {
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 170;
    const margin = 8;
    const left = Math.min(Math.max(margin, rect.right - menuWidth), Math.max(margin, window.innerWidth - menuWidth - margin));
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - menuHeight - 6;
    const top = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    setActionMenuPosition({ left, top });
    setOpenActionMenuId(menuId);
  };

  useEffect(() => {
    if (!pendingAutoSelectObjectId) return;
    if (!createUnitsFetched) return;
    if (form.objectId !== pendingAutoSelectObjectId) return;
    setForm((prev) => ({
      ...prev,
      stimmrechtsart: resolveDefaultStimmrechtsart(createObjectStandardStimmrechtsart, createHasStimmrechte),
      stimmberechtigte_einheiten: createUnitRows.map((row) => row.unitId)
    }));
    setPendingAutoSelectObjectId(null);
  }, [pendingAutoSelectObjectId, createUnitsFetched, form.objectId, createUnitRows, createHasStimmrechte, createObjectStandardStimmrechtsart]);

  useEffect(() => {
    if (form.stimmrechtsart === "stimmrecht" && !createCanUseStimmrecht) {
      setForm((prev) => ({ ...prev, stimmrechtsart: resolveDefaultStimmrechtsart(createObjectStandardStimmrechtsart, createHasStimmrechte) }));
    }
  }, [form.stimmrechtsart, createCanUseStimmrecht, createObjectStandardStimmrechtsart, createHasStimmrechte]);

  useEffect(() => {
    if (!editForm) return;
    if (editForm.stimmrechtsart === "stimmrecht" && !editCanUseStimmrecht) {
      setEditForm((prev) =>
        prev ? { ...prev, stimmrechtsart: resolveDefaultStimmrechtsart(editObjectStandardStimmrechtsart, editHasStimmrechte) } : prev
      );
    }
  }, [editForm, editCanUseStimmrecht, editObjectStandardStimmrechtsart, editHasStimmrechte]);

  useEffect(() => {
    if (faciliooEnabled) return;
    setForm((prev) =>
      prev.trigger_notifications_channel === "facilioo" ? { ...prev, trigger_notifications_channel: "email" } : prev
    );
    setEditForm((prev) =>
      prev && prev.trigger_notifications_channel === "facilioo" ? { ...prev, trigger_notifications_channel: "email" } : prev
    );
  }, [faciliooEnabled]);

  useEffect(() => {
    setCreateFaciliooValidationError("");
    setCreateFaciliooProcessTitle("");
  }, [form.objectId, form.facilioo_vorgangsnummer]);

  useEffect(() => {
    setEditFaciliooValidationError("");
    setEditFaciliooProcessTitle("");
  }, [editForm?.objectId, editForm?.facilioo_vorgangsnummer]);

  const createHasVorbeschlussTop = !!form.vorbeschluss_top.trim();
  const createHasVorbeschlussDate = !!form.vorbeschluss_wev_datum;
  const createVorbeschlussPairValid = createHasVorbeschlussTop === createHasVorbeschlussDate;
  const createVorbeschlussValid = createVorbeschlussPairValid;
  const editHasVorbeschlussTop = !!editForm?.vorbeschluss_top.trim();
  const editHasVorbeschlussDate = !!editForm?.vorbeschluss_wev_datum;
  const editVorbeschlussPairValid = editHasVorbeschlussTop === editHasVorbeschlussDate;
  const editVorbeschlussValid = editVorbeschlussPairValid;
  const createFaciliooRefFilled = !!form.facilioo_vorgangsnummer.trim();
  const editFaciliooRefFilled = !!editForm?.facilioo_vorgangsnummer.trim();
  const createFaciliooValid = !createFaciliooRefFilled || (!!createFaciliooProcessTitle && !createFaciliooValidationError);
  // Beim Bearbeiten darf der Speichern-Button nicht blockieren, nur weil der
  // Vorgangstitel noch nicht vorab geladen wurde. Die finale Prüfung läuft in
  // `validateEditFaciliooProcess()` beim tatsächlichen Speichern.
  const editFaciliooValid = !editFaciliooRefFilled || !editFaciliooValidationError;
  const canCreate = !!(form.objectId && form.titel.trim() && form.beschlusstext.trim() && form.stimmberechtigte_einheiten.length && !createDateErrors.startError && !createDateErrors.endError && createVorbeschlussValid && createFaciliooValid);
  const canEdit = !!(editForm && editForm.objectId && editForm.titel.trim() && editForm.beschlusstext.trim() && editForm.stimmberechtigte_einheiten.length && !editDateErrors.startError && !editDateErrors.endError && editVorbeschlussValid && editFaciliooValid);
  const resolutionNumberHint = "Wird systemseitig vergeben";
  const createResolutionNumber = nextResolutionNumberData?.beschlussnummer ?? "Wird beim Speichern vergeben";
  const rawRows = data?.data ?? [];
  const rows = useMemo(() => {
    if (sortBy !== "restlaufzeit") return rawRows;
    const sorted = [...rawRows].sort((a, b) => {
      const aDays = getRemainingDays(a.endedatum);
      const bDays = getRemainingDays(b.endedatum);
      if (aDays === null && bDays === null) return a.beschlussnummer.localeCompare(b.beschlussnummer, "de");
      if (aDays === null) return 1;
      if (bDays === null) return -1;
      if (aDays !== bDays) return aDays - bDays;
      return a.beschlussnummer.localeCompare(b.beschlussnummer, "de");
    });
    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [rawRows, sortBy, sortDir]);
  const totalRows = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    if (!data) return;
    const nextPage = page + 1;
    if (nextPage > totalPages) return;
    void qc.prefetchQuery({
      queryKey: ["resolutions", nextPage, pageSize, debouncedSearch, statusFilter, resultFilter, sortBy, sortDir],
      queryFn: () =>
        api.resolutions({
          page: nextPage,
          pageSize,
          search: debouncedSearch,
          status: statusFilter || undefined,
          ergebnis: resultFilter || undefined,
          sortBy: sortBy === "restlaufzeit" ? "objektnummer" : sortBy,
          sortDir
        }),
      staleTime: 3 * 60_000
    });
  }, [data, page, totalPages, pageSize, debouncedSearch, statusFilter, resultFilter, sortBy, sortDir, qc]);
  const isFrameworkV2 =
    uiSettingsData?.management?.uiFramework === "mockup_v2" ||
    (typeof document !== "undefined" && document.body.classList.contains("framework-v2"));
  const fixedViewportListLayout = isFrameworkV2 && !showCreate && !editForm;
  const pageItems = useMemo(() => {
    if (totalPages <= 1) return [1];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, start + 2);
    const adjustedStart = Math.max(1, end - 2);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  }, [page, totalPages]);
  const resetListFilters = () => {
    setSearch("");
    setStatusFilter("");
    setResultFilter("");
    setSortBy("objektnummer");
    setSortDir("asc");
    setPage(1);
  };

  const renderSortIndicator = (field: ResolutionSortField) => (
    <span className="resolutions-v2-sort-indicator" aria-hidden="true">
      {sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const renderSortableHeaderText = (field: ResolutionSortField, label: string) => (
    <span className="resolutions-v2-th-sort-copy">
      <span className="resolutions-v2-th-label">{label}</span>
      {renderSortIndicator(field)}
    </span>
  );

  const validateCreateFaciliooProcess = async (): Promise<boolean> => {
    const processRef = form.facilioo_vorgangsnummer.trim();
    if (!processRef) {
      setCreateFaciliooValidationError("");
      setCreateFaciliooProcessTitle("");
      return true;
    }
    if (createFaciliooProcessTitle && !createFaciliooValidationError) {
      return true;
    }
    if (!form.objectId) {
      setCreateFaciliooValidationError("Bitte zuerst ein Objekt auswählen.");
      setCreateFaciliooProcessTitle("");
      return false;
    }
    setCreateFaciliooValidationPending(true);
    setCreateFaciliooValidationError("");
    try {
      const result = await api.validateFaciliooProcessForObject(form.objectId, processRef);
      const resolvedTitle = String(result.processTitle ?? "").trim();
      setCreateFaciliooProcessTitle(resolvedTitle || "(ohne Titel)");
      return true;
    } catch (error) {
      setCreateFaciliooProcessTitle("");
      setCreateFaciliooValidationError(normalizeValidationErrorMessage(error));
      return false;
    } finally {
      setCreateFaciliooValidationPending(false);
    }
  };

  const validateEditFaciliooProcess = async (): Promise<boolean> => {
    const processRef = editForm?.facilioo_vorgangsnummer.trim() ?? "";
    if (!processRef) {
      setEditFaciliooValidationError("");
      setEditFaciliooProcessTitle("");
      return true;
    }
    if (editFaciliooProcessTitle && !editFaciliooValidationError) {
      return true;
    }
    if (!editForm?.objectId) {
      setEditFaciliooValidationError("Bitte zuerst ein Objekt auswählen.");
      setEditFaciliooProcessTitle("");
      return false;
    }
    setEditFaciliooValidationPending(true);
    setEditFaciliooValidationError("");
    try {
      const result = await api.validateFaciliooProcessForObject(editForm.objectId, processRef);
      const resolvedTitle = String(result.processTitle ?? "").trim();
      setEditFaciliooProcessTitle(resolvedTitle || "(ohne Titel)");
      return true;
    } catch (error) {
      setEditFaciliooProcessTitle("");
      setEditFaciliooValidationError(normalizeValidationErrorMessage(error));
      return false;
    } finally {
      setEditFaciliooValidationPending(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!canCreateResolutions) throw new Error("Keine Berechtigung zum Anlegen von Umlaufbeschlüssen.");
      if (!canCreate) throw new Error("Bitte alle Pflichtfelder ausfüllen");
      if (!(await validateCreateFaciliooProcess())) {
        throw new Error("Facilioo Vorgang konnte nicht validiert werden.");
      }
      const created = await api.createResolution({
        objectId: form.objectId,
        titel: form.titel.trim(),
        startdatum: form.startdatum || undefined,
        endedatum: form.endedatum || undefined,
        beschlusstext: form.beschlusstext.trim(),
        facilioo_vorgangsnummer: faciliooEnabled ? parseFacilioo(form.facilioo_vorgangsnummer) : undefined,
        dispatch_automation_enabled: form.dispatch_automation_enabled,
        trigger_notifications_enabled: form.trigger_notifications_enabled,
        trigger_notifications_channel: form.trigger_notifications_channel,
        trigger_notifications_events: normalizeTriggerNotificationEvents(form.trigger_notifications_events),
        stimmrechtsart: form.stimmrechtsart,
        quorum: form.quorum,
        vorbeschluss_top: form.vorbeschluss_top.trim() || undefined,
        vorbeschluss_wev_datum: form.vorbeschluss_wev_datum || undefined,
        stimmberechtigte_einheiten: form.stimmberechtigte_einheiten
      });
      if (createDocuments.length > 0) {
        await api.uploadResolutionDocuments(created.id, createDocuments);
      }
      return created;
    },
    onSuccess: async () => {
      setShowCreate(false);
      setForm(buildEmptyResolutionForm(faciliooEnabled));
      setCreateOwnerSearch("");
      setCreateUnitTypeFilter("");
      setCreateEntranceFilter("");
      setCreateDocuments([]);
      setCreateFaciliooValidationError("");
      setCreateFaciliooProcessTitle("");
      setCreateError("");
      await qc.invalidateQueries({ queryKey: ["resolutions"] });
    },
    onError: (e) => setCreateError(e instanceof Error ? e.message : "Umlaufbeschluss konnte nicht angelegt werden")
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editForm || !canEdit) throw new Error("Bitte alle Pflichtfelder ausfüllen");
      if (!(await validateEditFaciliooProcess())) {
        throw new Error("Facilioo Vorgang konnte nicht validiert werden.");
      }
      return api.updateResolution(editForm.id, {
        objectId: editForm.objectId,
        titel: editForm.titel.trim(),
        startdatum: editForm.startdatum || undefined,
        endedatum: editForm.endedatum || undefined,
        beschlusstext: editForm.beschlusstext.trim(),
        facilioo_vorgangsnummer: faciliooEnabled ? parseFacilioo(editForm.facilioo_vorgangsnummer) : undefined,
        dispatch_automation_enabled: editForm.dispatch_automation_enabled,
        trigger_notifications_enabled: editForm.trigger_notifications_enabled,
        trigger_notifications_channel: editForm.trigger_notifications_channel,
        trigger_notifications_events: normalizeTriggerNotificationEvents(editForm.trigger_notifications_events),
        stimmrechtsart: editForm.stimmrechtsart,
        quorum: editForm.quorum,
        vorbeschluss_top: editForm.vorbeschluss_top.trim() || undefined,
        vorbeschluss_wev_datum: editForm.vorbeschluss_wev_datum || undefined,
        stimmberechtigte_einheiten: editForm.stimmberechtigte_einheiten
      });
    },
    onSuccess: async () => {
      setEditForm(null);
      setEditOwnerSearch("");
      setEditUnitTypeFilter("");
      setEditEntranceFilter("");
      setEditFaciliooValidationError("");
      setEditFaciliooProcessTitle("");
      setEditError("");
      await qc.invalidateQueries({ queryKey: ["resolutions"] });
    },
    onError: (e) => setEditError(e instanceof Error ? e.message : "Umlaufbeschluss konnte nicht bearbeitet werden")
  });

  const startMutation = useMutation({
    mutationFn: async (payload: { id: string; endedatum?: string }) => {
      if (payload.endedatum) {
        await api.updateResolution(payload.id, { endedatum: payload.endedatum });
      }
      return api.startResolution(payload.id);
    },
    onSuccess: async () => {
      setStartConfirm(null);
      setActionError("");
      await qc.invalidateQueries({ queryKey: ["resolutions"] });
    },
    onError: (e) => setActionError(e instanceof Error ? e.message : "Starten fehlgeschlagen")
  });
  const stopMutation = useMutation({ mutationFn: (id: string) => api.stopResolution(id), onSuccess: async () => { setStopConfirm(null); setActionError(""); await qc.invalidateQueries({ queryKey: ["resolutions"] }); }, onError: (e) => setActionError(e instanceof Error ? e.message : "Stoppen fehlgeschlagen") });
  const deleteMutation = useMutation({ mutationFn: (id: string) => api.deleteResolution(id), onSuccess: async () => { setDeleteConfirm(null); setActionError(""); await qc.invalidateQueries({ queryKey: ["resolutions"] }); }, onError: (e) => setActionError(e instanceof Error ? e.message : "Löschen fehlgeschlagen") });

  const cancelCreate = () => {
    setForm(buildEmptyResolutionForm(faciliooEnabled));
    setCreateOwnerSearch("");
    setCreateUnitTypeFilter("");
    setCreateEntranceFilter("");
    setCreateDocuments([]);
    setCreateFaciliooValidationError("");
    setCreateFaciliooProcessTitle("");
    setCreateError("");
    setShowCreate(false);
  };

  const toggleSort = (field: ResolutionSortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDir("asc");
  };

  const sortLabel = (field: ResolutionSortField, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6
      }}
      title={sortBy === field ? `Sortierung: ${sortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Sortierung aktivieren"}
    >
      <span>{label}</span>
      <span aria-hidden="true">{sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );
  const autoDispatchTooltip = "Automatischer Versandprozess aktiv";

  const columns = useMemo(() => [
    {
      key: "beschlussnummer",
      label: sortLabel("beschlussnummer", "Beschluss-ID"),
      render: (r: ResolutionRow) => (
        <Link className="badge resolution-number" to={`/resolutions/${r.id}`} title={r.beschlussnummer}>
          {r.beschlussnummer}
        </Link>
      )
    },
    {
      key: "objektnummer",
      label: sortLabel("objektnummer", "Objekt‑Nr"),
      width: 96,
      align: "center" as const,
      render: (r: ResolutionRow) => {
        const resolvedObjectNumber = (r.objektnummer || objectById.get(r.objectId)?.objektnummer || "-").toString();
        return (
          <Link className="badge object-number" to={`/objects/${r.objectId}`} title={resolvedObjectNumber} style={{ textDecoration: "none" }}>
            {resolvedObjectNumber}
          </Link>
        );
      }
    },
    {
      key: "objektname",
      label: sortLabel("objektname", "Objektname"),
      render: (r: ResolutionRow) => r.objektname || objectById.get(r.objectId)?.objektname || "-"
    },
    { key: "titel", label: sortLabel("titel", "Titel") },
    { key: "stimmrechtsart", label: "Stimmrechtsart", align: "center" as const, render: (r: ResolutionRow) => formatStimmrechtsart(r.stimmrechtsart) },
    { key: "quorum", label: "Quorum", width: 92, render: (r: ResolutionRow) => formatQuorum(r.quorum) },
    {
      key: "status",
      label: sortLabel("status", "Status"),
      align: "center" as const,
      render: (r: ResolutionRow) => (
        <span className="resolution-status-wrap">
          <span className={`badge ${r.status}`}>{formatStatus(r.status)}</span>
          {r.dispatch_automation_enabled && r.status === "aktiv" ? (
            <span
              className="dispatch-automation-indicator quick-tooltip"
              data-tooltip={autoDispatchTooltip}
              aria-label={autoDispatchTooltip}
              tabIndex={0}
            >
              ⚙
            </span>
          ) : null}
        </span>
      )
    },
    { key: "startdatum", label: "Startdatum", align: "center" as const, render: (r: ResolutionRow) => formatDateDE(r.startdatum) },
    { key: "endedatum", label: "Enddatum", align: "center" as const, render: (r: ResolutionRow) => formatDateDE(r.endedatum) },
    {
      key: "restlaufzeit",
      label: sortLabel("restlaufzeit", "Restlaufzeit"),
      align: "center" as const,
      render: (r: ResolutionRow) => {
        const remainingDays = getRemainingDays(r.endedatum);
        return remainingDays === null ? "-" : `${remainingDays} Tage`;
      }
    },
    { key: "ruecklaufquote", label: sortLabel("ruecklaufquote", "Rücklaufquote"), align: "center" as const, render: (r: ResolutionRow) => `${r.ruecklaufquote.toFixed(2)} %` },
    { key: "ergebnis", label: "Ergebnis", align: "center" as const, render: (r: ResolutionRow) => { const o = getOutcome(r); return o.tone ? <span className={`badge ergebnis-${o.tone === "success" ? "angenommen" : "abgelehnt"}`}>{o.label}</span> : <span className="badge ergebnis-neutral">{o.label}</span>; } },
    {
      key: "actions",
      label: "Aktionen",
      render: (r: ResolutionRow) => {
        const isActive = r.status === "aktiv";
        const isCompleted = r.status === "abgeschlossen";
        const canEdit = r.status === "entwurf";
        const hasVotes = (r.abgegebene_stimmen ?? 0) > 0;
        const blockedByCompletedPermission = isCompleted && !canDeleteCompletedResolutions;
        const canDeleteCompletedWithVotes = isCompleted && canDeleteCompletedResolutions;
        const blockedByMissingDeletePermission = !isCompleted && !canDeleteResolutions;
        const blockedByVotes = hasVotes && !canDeleteCompletedWithVotes;
        return <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canEditResolutions ? (
            <button
              onClick={() => {
                if (!canEdit) { setActionError("Nur Entwürfe können bearbeitet werden. Laufende oder beendete Beschlüsse sind gesperrt."); return; }
                setEditOwnerSearch("");
                setEditUnitTypeFilter("");
                setEditEntranceFilter("");
                setEditForm({ id: r.id, objectId: r.objectId, beschlussnummer: r.beschlussnummer, titel: r.titel, startdatum: r.startdatum ?? "", endedatum: r.endedatum ?? "", stimmrechtsart: r.stimmrechtsart, quorum: r.quorum, vorbeschluss_top: r.vorbeschluss_top ?? "", vorbeschluss_wev_datum: r.vorbeschluss_wev_datum ?? "", beschlusstext: r.beschlusstext, facilioo_vorgangsnummer: r.facilioo_vorgangsnummer ? String(r.facilioo_vorgangsnummer) : "", dispatch_automation_enabled: r.dispatch_automation_enabled ?? false, trigger_notifications_enabled: r.trigger_notifications_enabled ?? false, trigger_notifications_channel: r.trigger_notifications_channel ?? "email", trigger_notifications_events: normalizeTriggerNotificationEvents(r.trigger_notifications_events), stimmberechtigte_einheiten: r.stimmberechtigte_einheiten ?? [] });
              }}
              disabled={!canEdit}
            >Bearbeiten</button>
          ) : null}
          {r.status === "entwurf" ? <button className="resolution-action-btn resolution-start-btn" onClick={() => {
            if (!r.endedatum) {
              setStartConfirm({ id: r.id, beschlussnummer: r.beschlussnummer, endedatum: "", error: "" });
              return;
            }
            startMutation.mutate({ id: r.id, endedatum: r.endedatum });
          }} disabled={startMutation.isPending}>Starten</button> : null}
          {r.status === "aktiv" ? <button className="resolution-action-btn resolution-stop-btn" onClick={() => setStopConfirm({ id: r.id, beschlussnummer: r.beschlussnummer, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" })} disabled={stopMutation.isPending}>Stoppen</button> : null}
          {canDeleteAnyResolution ? (
            <button disabled={isActive || blockedByMissingDeletePermission || blockedByVotes || blockedByCompletedPermission || deleteMutation.isPending} onClick={() => {
              if (isActive) { setActionError("Aktive Beschlüsse können nicht gelöscht werden."); return; }
              if (blockedByMissingDeletePermission) { setActionError("Keine Berechtigung zum Löschen dieses Beschlusses."); return; }
              if (blockedByVotes) { setActionError("Beschlüsse mit Stimmabgaben können nicht gelöscht werden."); return; }
              if (blockedByCompletedPermission) { setActionError("Keine Berechtigung zum Löschen abgeschlossener Beschlüsse."); return; }
              setDeleteConfirm({ id: r.id, beschlussnummer: r.beschlussnummer, code: String(Math.floor(1000 + Math.random() * 9000)), input: ["", "", "", ""], error: "" });
            }}>Löschen</button>
          ) : null}
          {isActive ? <span className="lock-indicator" title="Aktiver Beschluss">{"\uD83D\uDD12"}</span> : null}
        </div>;
      }
    }
  ], [autoDispatchTooltip, canDeleteAnyResolution, canDeleteCompletedResolutions, canDeleteResolutions, canEditResolutions, deleteMutation.isPending, objectById, sortBy, sortDir, startMutation.isPending, stopMutation.isPending]);

  return <>
    <div className={fixedViewportListLayout ? "resolutions-v2-page" : undefined}>
    {!showCreate && !editForm ? (
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header" : ""}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-icon" aria-hidden="true">
              <ResolutionMenuIcon />
            </span>
          ) : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Umlaufbeschlüsse</span>
              <span className="resolutions-v2-title-sub">Übersicht aller Umlaufbeschlüsse und Ergebnisse</span>
            </span>
          ) : (
            <span>Umlaufbeschlüsse</span>
          )}
        </h2>
        {canCreateResolutions ? (
          <button
            className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""}
            onClick={() => {
              setForm(buildEmptyResolutionForm(faciliooEnabled));
              setCreateOwnerSearch("");
              setCreateUnitTypeFilter("");
              setCreateEntranceFilter("");
              setCreateDocuments([]);
              setCreateFaciliooValidationError("");
              setCreateFaciliooProcessTitle("");
              setCreateError("");
              setShowCreate(true);
            }}
          >
            {isFrameworkV2 ? (
              <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            ) : null}
            <span>Neuer Beschluss</span>
          </button>
        ) : null}
      </section>
    ) : null}

    {canCreateResolutions && showCreate ? <section className={`panel create-form resolution-create-form${isFrameworkV2 ? " resolution-create-form-v2" : ""}`}>
      <div className={isFrameworkV2 ? "resolution-create-v2-head" : undefined}>
        {isFrameworkV2 ? (
          <span className="resolution-create-v2-head-icon" aria-hidden="true">
            <ResolutionCreateSectionIcon name="resolution" />
          </span>
        ) : null}
        <div className={isFrameworkV2 ? "resolution-create-v2-head-copy" : undefined}>
          <h3 style={isFrameworkV2 ? undefined : { marginTop: 0 }}>Umlaufbeschluss anlegen</h3>
          {isFrameworkV2 ? <p>Stammdaten, Dokumente und stimmberechtigte Einheiten erfassen</p> : null}
        </div>
      </div>
      <div className="form-grid">
        <div className="resolution-main-fields-box">
        <div className="form-field"><label>Objekt*</label><select value={form.objectId} onChange={(e) => { const objectId = e.target.value; const objectDefault = (objectsData?.data ?? []).find((o) => o.id === objectId)?.standard_stimmrechtsart; setForm({ ...form, objectId, stimmrechtsart: objectDefault ?? "kopf", stimmberechtigte_einheiten: [] }); setCreateOwnerSearch(""); setPendingAutoSelectObjectId(objectId || null); setCreateFaciliooValidationError(""); setCreateFaciliooProcessTitle(""); }}><option value="">Objekt wählen*</option>{(objectsData?.data ?? []).map((o) => <option key={o.id} value={o.id}>{`${o.objektnummer} - ${o.objektname}`}</option>)}</select></div>
        <div className="form-field">
          <label>Beschlussnummer <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>{resolutionNumberHint}</small></label>
          <input value={createResolutionNumber} disabled />
        </div>
        {faciliooEnabled ? (
          <div className="form-field">
            <label>Facilioo Vorgangsnummer</label>
            <input
              type="number"
              min={0}
              max={9999999999}
              value={form.facilioo_vorgangsnummer}
              onChange={(e) => setForm({ ...form, facilioo_vorgangsnummer: e.target.value })}
              onBlur={() => {
                void validateCreateFaciliooProcess();
              }}
            />
            {createFaciliooValidationPending ? <small style={{ color: "var(--text-soft)" }}>Vorgang wird geprüft ...</small> : null}
            {createFaciliooProcessTitle ? <small style={{ color: "var(--text-soft)" }}>Vorgangsbetreff: {createFaciliooProcessTitle}</small> : null}
            {createFaciliooValidationError ? <small className="error-text">{createFaciliooValidationError}</small> : null}
          </div>
        ) : null}
        <div className="form-field"><label>Startdatum (optional)</label><input type="date" value={form.startdatum} onChange={(e) => setForm({ ...form, startdatum: e.target.value })} />{createDateErrors.startError ? <span className="error-text">{createDateErrors.startError}</span> : null}</div>
        <div className="form-field"><label>Enddatum (optional)</label><input type="date" value={form.endedatum} onChange={(e) => setForm({ ...form, endedatum: e.target.value })} />{createDateErrors.endError ? <span className="error-text">{createDateErrors.endError}</span> : null}</div>
        <div className="form-field"><label>Stimmrechtsart*</label><select value={form.stimmrechtsart} onChange={(e) => setForm({ ...form, stimmrechtsart: e.target.value as Stimmrechtsart })}><option value="kopf">Kopfstimmen</option>{createCanUseStimmrecht ? <option value="stimmrecht">Stimmrecht</option> : null}<option value="mea">MEA</option></select></div>
        <div className="form-field"><label>Quorum*</label><select value={form.quorum} onChange={(e) => { const quorum = e.target.value as QuorumArt; setForm({ ...form, quorum, ...(quorum === "allstimmig" ? { vorbeschluss_top: "", vorbeschluss_wev_datum: "" } : {}) }); }}><option value="allstimmig">Allstimmig</option><option value="einfache_mehrheit">Einfache Mehrheit</option><option value="qualifizierte_mehrheit">Qualifizierte Mehrheit</option></select></div>
        <div className="form-field">
          <label>Automatischen Versandprozess aktivieren</label>
          <div className="settings-switch-row" style={{ marginBottom: 10 }}>
            <label className="settings-toggle" htmlFor="resolution-create-dispatch-enabled" aria-label="Automatischen Versandprozess aktivieren oder deaktivieren">
              <input
                id="resolution-create-dispatch-enabled"
                type="checkbox"
                checked={form.dispatch_automation_enabled}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dispatch_automation_enabled: e.target.checked
                  })
                }
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>
        </div>
        {isVorbeschlussRequired(form.quorum) ? (
          <div className="form-field">
            <label>TOP der WEV (optional)</label>
            <input value={form.vorbeschluss_top} onChange={(e) => setForm({ ...form, vorbeschluss_top: e.target.value })} />
            <small style={{ color: "var(--text-soft)" }}>Tagesordnungspunkt des Vorbeschlusses, mit dem das abgesenkte Quorum beschlossen wurde.</small>
          </div>
        ) : null}
        {isVorbeschlussRequired(form.quorum) ? (
          <div className="form-field">
            <label>Datum der WEV (optional)</label>
            <input type="date" value={form.vorbeschluss_wev_datum} onChange={(e) => setForm({ ...form, vorbeschluss_wev_datum: e.target.value })} />
            <small style={{ color: "var(--text-soft)" }}>Datum der WEG-Versammlung des Vorbeschlusses.</small>
          </div>
        ) : null}
        <div className="form-field form-field-wide">
          <label>Ablauf Info's an Verwaltung senden</label>
          <div className="settings-switch-row" style={{ marginBottom: 10 }}>
            <label className="settings-toggle" htmlFor="resolution-create-trigger-enabled" aria-label="Ablauf Infos an Verwaltung senden aktivieren oder deaktivieren">
              <input
                id="resolution-create-trigger-enabled"
                type="checkbox"
                checked={form.trigger_notifications_enabled}
                onChange={(e) =>
                  setForm({
                    ...form,
                    trigger_notifications_enabled: e.target.checked
                  })
                }
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>
          {form.trigger_notifications_enabled ? (
            <>
              <div className="form-field" style={{ padding: 0, marginBottom: 10 }}>
                <label>Versandkanal</label>
                <select
                  value={form.trigger_notifications_channel}
                  onChange={(e) => setForm({ ...form, trigger_notifications_channel: e.target.value as TriggerNotificationChannel })}
                >
                  <option value="email">E-Mail</option>
                  {faciliooEnabled ? <option value="facilioo">Facilioo Vorgang</option> : null}
                </select>
              </div>
              <div className="resolution-trigger-events-box">
                <div className="resolution-trigger-events-actions">
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        trigger_notifications_events: allTriggerNotificationEvents()
                      })
                    }
                  >
                    Alles markieren
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        trigger_notifications_events: defaultTriggerNotificationEvents()
                      })
                    }
                  >
                    Alle demarkieren
                  </button>
                </div>
                <div className="resolution-trigger-events-grid">
                {TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => (
                  <label key={eventOption.key} className="resolution-trigger-event-option">
                    <input
                      type="checkbox"
                      checked={Boolean(form.trigger_notifications_events[eventOption.key])}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          trigger_notifications_events: {
                            ...form.trigger_notifications_events,
                            [eventOption.key]: e.target.checked
                          }
                        })
                      }
                    />
                    <span>{eventOption.label}</span>
                  </label>
                ))}
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className="form-field form-field-wide"><label>Titel*</label><input value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} /></div>
        <div className="form-field form-field-wide"><label>Beschlusstext*</label><textarea rows={8} value={form.beschlusstext} onChange={(e) => setForm({ ...form, beschlusstext: e.target.value })} /></div>
        {!createVorbeschlussPairValid ? <div className="form-field form-field-wide"><span className="error-text">TOP und Datum der WEV müssen gemeinsam befüllt werden.</span></div> : null}
        </div>
        <div className="form-field form-field-wide resolution-upload-box">
          <div className={isFrameworkV2 ? "resolution-create-v2-section-head" : undefined}>
            {isFrameworkV2 ? (
              <span className="resolution-create-v2-section-icon" aria-hidden="true">
                <ResolutionCreateSectionIcon name="upload" />
              </span>
            ) : null}
            <label>Dokumente (optional)</label>
          </div>
          <div
            className={`resolution-document-dropzone${createDocumentsDropActive ? " is-drag-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = "copy";
              setCreateDocumentsDropActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setCreateDocumentsDropActive(false);
            }}
            onDrop={onCreateDocumentsDrop}
          >
            <input
              ref={createDocumentsInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png"
              style={{ display: "none" }}
              onChange={(e) => mergeCreateDocuments(Array.from(e.target.files ?? []), true)}
            />
            <div className="resolution-document-dropzone-text">Dateien hier hineinziehen oder über den Button auswählen.</div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
            <button type="button" onClick={() => createDocumentsInputRef.current?.click()}>
              Dateien auswählen
            </button>
            <button type="button" onClick={() => setCreateDocuments([])} disabled={!createDocuments.length}>
              Auswahl leeren
            </button>
            <span style={{ color: "var(--text-soft)", fontSize: 12 }}>
              {createDocuments.length ? `${createDocuments.length} Datei(en) in der Liste` : "Keine Dateien ausgewählt"}
            </span>
          </div>
          <small style={{ color: "var(--text-soft)" }}>Erlaubte Formate: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG. Maximal 20 Dateien pro Upload, maximal 20 MB je Datei.</small>
        </div>
        {renderUnitSelector(
          createUnitRows,
          form.stimmberechtigte_einheiten,
          createOwnerSearch,
          setCreateOwnerSearch,
          createUnitTypeFilter,
          setCreateUnitTypeFilter,
          createEntranceFilter,
          setCreateEntranceFilter,
          (unitId, checked) => setForm({ ...form, stimmberechtigte_einheiten: checked ? Array.from(new Set([...form.stimmberechtigte_einheiten, unitId])) : form.stimmberechtigte_einheiten.filter((v) => v !== unitId) }),
          () => setForm({ ...form, stimmberechtigte_einheiten: createUnitRows.map((u) => u.unitId) }),
          () => setForm({ ...form, stimmberechtigte_einheiten: [] }),
          () => {
            const filteredIds = getFilteredResolutionUnitIds(createUnitRows, createOwnerSearch, createUnitTypeFilter, createEntranceFilter);
            setForm({ ...form, stimmberechtigte_einheiten: Array.from(new Set([...form.stimmberechtigte_einheiten, ...filteredIds])) });
          },
          () => {
            const filteredIds = getFilteredResolutionUnitIds(createUnitRows, createOwnerSearch, createUnitTypeFilter, createEntranceFilter);
            setForm({ ...form, stimmberechtigte_einheiten: form.stimmberechtigte_einheiten.filter((id) => !filteredIds.includes(id)) });
          },
          isFrameworkV2
        )}
      </div>
      <div className="form-actions resolution-create-actions"><button className="resolution-primary-btn" disabled={!canCreate || createMutation.isPending} onClick={() => createMutation.mutate()}>{createMutation.isPending ? "Speichere..." : "Umlaufbeschluss speichern"}</button><button type="button" className="resolution-secondary-btn" onClick={cancelCreate}>Abbrechen</button>{createError ? <span className="error-text">{createError}</span> : null}</div>
    </section> : null}

    {!showCreate && canEditResolutions && editForm ? <section className={`panel create-form resolution-create-form${isFrameworkV2 ? " resolution-create-form-v2" : ""}`}>
      <div className={isFrameworkV2 ? "resolution-create-v2-head" : undefined}>
        {isFrameworkV2 ? (
          <span className="resolution-create-v2-head-icon" aria-hidden="true">
            <ResolutionCreateSectionIcon name="resolution" />
          </span>
        ) : null}
        <div className={isFrameworkV2 ? "resolution-create-v2-head-copy" : undefined}>
          <h3 style={isFrameworkV2 ? undefined : { marginTop: 0 }}>Umlaufbeschluss bearbeiten</h3>
          {isFrameworkV2 ? <p>Stammdaten, Dokumente und stimmberechtigte Einheiten bearbeiten</p> : null}
        </div>
      </div>
      <div className="form-grid">
        <div className="resolution-main-fields-box">
        <div className="form-field"><label>Objekt*</label><select value={editForm.objectId} onChange={(e) => { const objectId = e.target.value; const objectDefault = (objectsData?.data ?? []).find((o) => o.id === objectId)?.standard_stimmrechtsart; setEditForm({ ...editForm, objectId, stimmrechtsart: objectDefault ?? editForm.stimmrechtsart, stimmberechtigte_einheiten: [] }); setEditOwnerSearch(""); setEditFaciliooValidationError(""); setEditFaciliooProcessTitle(""); }}><option value="">Objekt wählen*</option>{(objectsData?.data ?? []).map((o) => <option key={o.id} value={o.id}>{`${o.objektnummer} - ${o.objektname}`}</option>)}</select></div>
        <div className="form-field">
          <label>Beschlussnummer <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>{resolutionNumberHint}</small></label>
          <input value={editForm.beschlussnummer} disabled />
        </div>
        {faciliooEnabled ? (
          <div className="form-field">
            <label>Facilioo Vorgangsnummer</label>
            <input
              type="number"
              min={0}
              max={9999999999}
              value={editForm.facilioo_vorgangsnummer}
              onChange={(e) => setEditForm({ ...editForm, facilioo_vorgangsnummer: e.target.value })}
              onBlur={() => {
                void validateEditFaciliooProcess();
              }}
            />
            {editFaciliooValidationPending ? <small style={{ color: "var(--text-soft)" }}>Vorgang wird geprüft ...</small> : null}
            {editFaciliooProcessTitle ? <small style={{ color: "var(--text-soft)" }}>Vorgangsbetreff: {editFaciliooProcessTitle}</small> : null}
            {editFaciliooValidationError ? <small className="error-text">{editFaciliooValidationError}</small> : null}
          </div>
        ) : null}
        <div className="form-field"><label>Startdatum (optional)</label><input type="date" value={editForm.startdatum} onChange={(e) => setEditForm({ ...editForm, startdatum: e.target.value })} />{editDateErrors.startError ? <span className="error-text">{editDateErrors.startError}</span> : null}</div>
        <div className="form-field"><label>Enddatum (optional)</label><input type="date" value={editForm.endedatum} onChange={(e) => setEditForm({ ...editForm, endedatum: e.target.value })} />{editDateErrors.endError ? <span className="error-text">{editDateErrors.endError}</span> : null}</div>
        <div className="form-field"><label>Stimmrechtsart*</label><select value={editForm.stimmrechtsart} onChange={(e) => setEditForm({ ...editForm, stimmrechtsart: e.target.value as Stimmrechtsart })}><option value="kopf">Kopfstimmen</option>{editCanUseStimmrecht ? <option value="stimmrecht">Stimmrecht</option> : null}<option value="mea">MEA</option></select></div>
        <div className="form-field"><label>Quorum*</label><select value={editForm.quorum} onChange={(e) => { const quorum = e.target.value as QuorumArt; setEditForm({ ...editForm, quorum, ...(quorum === "allstimmig" ? { vorbeschluss_top: "", vorbeschluss_wev_datum: "" } : {}) }); }}><option value="allstimmig">Allstimmig</option><option value="einfache_mehrheit">Einfache Mehrheit</option><option value="qualifizierte_mehrheit">Qualifizierte Mehrheit</option></select></div>
        <div className="form-field">
          <label>Automatischen Versandprozess aktivieren</label>
          <div className="settings-switch-row" style={{ marginBottom: 10 }}>
            <label className="settings-toggle" htmlFor="resolution-edit-dispatch-enabled" aria-label="Automatischen Versandprozess aktivieren oder deaktivieren">
              <input
                id="resolution-edit-dispatch-enabled"
                type="checkbox"
                checked={editForm.dispatch_automation_enabled}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    dispatch_automation_enabled: e.target.checked
                  })
                }
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>
        </div>
        {isVorbeschlussRequired(editForm.quorum) ? (
          <div className="form-field">
            <label>TOP der WEV (optional)</label>
            <input value={editForm.vorbeschluss_top} onChange={(e) => setEditForm({ ...editForm, vorbeschluss_top: e.target.value })} />
            <small style={{ color: "var(--text-soft)" }}>Tagesordnungspunkt des Vorbeschlusses, mit dem das abgesenkte Quorum beschlossen wurde.</small>
          </div>
        ) : null}
        {isVorbeschlussRequired(editForm.quorum) ? (
          <div className="form-field">
            <label>Datum der WEV (optional)</label>
            <input type="date" value={editForm.vorbeschluss_wev_datum} onChange={(e) => setEditForm({ ...editForm, vorbeschluss_wev_datum: e.target.value })} />
            <small style={{ color: "var(--text-soft)" }}>Datum der WEG-Versammlung des Vorbeschlusses.</small>
          </div>
        ) : null}
        <div className="form-field form-field-wide">
          <label>Ablauf Info's an Verwaltung senden</label>
          <div className="settings-switch-row" style={{ marginBottom: 10 }}>
            <label className="settings-toggle" htmlFor="resolution-edit-trigger-enabled" aria-label="Ablauf Infos an Verwaltung senden aktivieren oder deaktivieren">
              <input
                id="resolution-edit-trigger-enabled"
                type="checkbox"
                checked={editForm.trigger_notifications_enabled}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    trigger_notifications_enabled: e.target.checked
                  })
                }
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>
          {editForm.trigger_notifications_enabled ? (
            <>
              <div className="form-field" style={{ padding: 0, marginBottom: 10 }}>
                <label>Versandkanal</label>
                <select
                  value={editForm.trigger_notifications_channel}
                  onChange={(e) => setEditForm({ ...editForm, trigger_notifications_channel: e.target.value as TriggerNotificationChannel })}
                >
                  <option value="email">E-Mail</option>
                  {faciliooEnabled ? <option value="facilioo">Facilioo Vorgang</option> : null}
                </select>
              </div>
              <div className="resolution-trigger-events-box">
                <div className="resolution-trigger-events-actions">
                  <button
                    type="button"
                    onClick={() =>
                      setEditForm({
                        ...editForm,
                        trigger_notifications_events: allTriggerNotificationEvents()
                      })
                    }
                  >
                    Alles markieren
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setEditForm({
                        ...editForm,
                        trigger_notifications_events: defaultTriggerNotificationEvents()
                      })
                    }
                  >
                    Alle demarkieren
                  </button>
                </div>
                <div className="resolution-trigger-events-grid">
                {TRIGGER_NOTIFICATION_EVENT_OPTIONS.map((eventOption) => (
                  <label key={eventOption.key} className="resolution-trigger-event-option">
                    <input
                      type="checkbox"
                      checked={Boolean(editForm.trigger_notifications_events[eventOption.key])}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          trigger_notifications_events: {
                            ...editForm.trigger_notifications_events,
                            [eventOption.key]: e.target.checked
                          }
                        })
                      }
                    />
                    <span>{eventOption.label}</span>
                  </label>
                ))}
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className="form-field form-field-wide"><label>Titel*</label><input value={editForm.titel} onChange={(e) => setEditForm({ ...editForm, titel: e.target.value })} /></div>
        <div className="form-field form-field-wide"><label>Beschlusstext*</label><textarea rows={8} value={editForm.beschlusstext} onChange={(e) => setEditForm({ ...editForm, beschlusstext: e.target.value })} /></div>
        {!editVorbeschlussPairValid ? <div className="form-field form-field-wide"><span className="error-text">TOP und Datum der WEV müssen gemeinsam befüllt werden.</span></div> : null}
        </div>
        {renderUnitSelector(
          editUnitRows,
          editForm.stimmberechtigte_einheiten,
          editOwnerSearch,
          setEditOwnerSearch,
          editUnitTypeFilter,
          setEditUnitTypeFilter,
          editEntranceFilter,
          setEditEntranceFilter,
          (unitId, checked) => setEditForm({ ...editForm, stimmberechtigte_einheiten: checked ? Array.from(new Set([...editForm.stimmberechtigte_einheiten, unitId])) : editForm.stimmberechtigte_einheiten.filter((v) => v !== unitId) }),
          () => setEditForm({ ...editForm, stimmberechtigte_einheiten: editUnitRows.map((u) => u.unitId) }),
          () => setEditForm({ ...editForm, stimmberechtigte_einheiten: [] }),
          () => {
            const filteredIds = getFilteredResolutionUnitIds(editUnitRows, editOwnerSearch, editUnitTypeFilter, editEntranceFilter);
            setEditForm({ ...editForm, stimmberechtigte_einheiten: Array.from(new Set([...editForm.stimmberechtigte_einheiten, ...filteredIds])) });
          },
          () => {
            const filteredIds = getFilteredResolutionUnitIds(editUnitRows, editOwnerSearch, editUnitTypeFilter, editEntranceFilter);
            setEditForm({ ...editForm, stimmberechtigte_einheiten: editForm.stimmberechtigte_einheiten.filter((id) => !filteredIds.includes(id)) });
          },
          isFrameworkV2
        )}
      </div>
      <div className="form-actions resolution-create-actions"><button className="resolution-primary-btn" disabled={!canEdit || editMutation.isPending} onClick={() => editMutation.mutate()}>{editMutation.isPending ? "Speichere..." : "Änderungen speichern"}</button><button type="button" className="resolution-secondary-btn" onClick={() => { setEditForm(null); setEditFaciliooValidationError(""); setEditFaciliooProcessTitle(""); }}>Abbrechen</button>{editError ? <span className="error-text">{editError}</span> : null}</div>
    </section> : null}

    <StartResolutionModal
      open={!!startConfirm}
      beschlussnummer={startConfirm?.beschlussnummer ?? ""}
      enddatum={startConfirm?.endedatum ?? ""}
      error={startConfirm?.error}
      isPending={startMutation.isPending}
      onClose={() => setStartConfirm(null)}
      onEnddatumChange={(value) => startConfirm && setStartConfirm({ ...startConfirm, endedatum: value, error: "" })}
      onConfirm={() => {
        if (!startConfirm) return;
        const today = getTodayDateKey();
        if (!startConfirm.endedatum) {
          setStartConfirm({ ...startConfirm, error: "Enddatum ist erforderlich." });
          return;
        }
        if (startConfirm.endedatum <= today) {
          setStartConfirm({ ...startConfirm, error: "Enddatum muss nach dem heutigen Startdatum liegen." });
          return;
        }
        startMutation.mutate({ id: startConfirm.id, endedatum: startConfirm.endedatum });
      }}
    />

    <DeleteCodeConfirmModal
      open={!!stopConfirm}
      title="Beschluss stoppen"
      message={stopConfirm ? `Beschluss ${stopConfirm.beschlussnummer} soll manuell gestoppt werden.` : ""}
      code={stopConfirm?.code ?? ""}
      input={stopConfirm?.input ?? ["", "", "", ""]}
      error={stopConfirm?.error}
      isPending={stopMutation.isPending}
      onClose={() => setStopConfirm(null)}
      onInputChange={(next) => stopConfirm && setStopConfirm({ ...stopConfirm, input: next })}
      onErrorReset={() => stopConfirm && stopConfirm.error && setStopConfirm({ ...stopConfirm, error: "" })}
      confirmLabel="Stoppen bestätigen"
      pendingLabel="Stoppe..."
      onConfirm={() => {
        if (!stopConfirm) return;
        if (stopConfirm.input.join("") !== stopConfirm.code) { setStopConfirm({ ...stopConfirm, error: "Sicherheitscode falsch eingegeben." }); return; }
        stopMutation.mutate(stopConfirm.id);
      }}
    />

    <DeleteCodeConfirmModal
      open={!!deleteConfirm}
      message={deleteConfirm ? `Beschluss ${deleteConfirm.beschlussnummer} soll gelöscht werden.` : ""}
      code={deleteConfirm?.code ?? ""}
      input={deleteConfirm?.input ?? ["", "", "", ""]}
      error={deleteConfirm?.error}
      isPending={deleteMutation.isPending}
      onClose={() => setDeleteConfirm(null)}
      onInputChange={(next) => deleteConfirm && setDeleteConfirm({ ...deleteConfirm, input: next })}
      onErrorReset={() => deleteConfirm && deleteConfirm.error && setDeleteConfirm({ ...deleteConfirm, error: "" })}
      onConfirm={() => {
        if (!deleteConfirm) return;
        if (deleteConfirm.input.join("") !== deleteConfirm.code) { setDeleteConfirm({ ...deleteConfirm, error: "Sicherheitscode falsch eingegeben." }); return; }
        deleteMutation.mutate(deleteConfirm.id);
      }}
    />

    {!showCreate && !editForm && actionError ? <section className="panel error-text">{actionError}</section> : null}
    {!showCreate && !editForm && isError ? <section className="panel">Fehler beim Laden der Umlaufbeschlüsse.</section> : null}
    {!showCreate && !editForm ? (
      isFrameworkV2 ? (
        <section className="panel resolutions-v2-list-panel">
          <div className="resolutions-v2-filterbar">
            <div className="resolutions-v2-filter-fields">
              <label className="resolutions-v2-filter-input">
                <span className="resolutions-v2-filter-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" fill="none">
                    <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
                    <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ResolutionStatusFilter)}>
                <option value="">Alle Status</option>
                <option value="entwurf">Entwurf</option>
                <option value="aktiv">Läuft</option>
                <option value="abgeschlossen">Beendet</option>
              </select>
              <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value as ResolutionResultFilter)}>
                <option value="">Alle Ergebnisse</option>
                <option value="angenommen">Angenommen</option>
                <option value="abgelehnt">Abgelehnt</option>
                <option value="offen">Offen</option>
              </select>
            </div>
            <button type="button" className="resolutions-v2-reset-btn" onClick={resetListFilters}>
              <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M5.2 6.3V3.9H2.8M4 4a6.4 6.4 0 1 1-1.4 7.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <span>Filter zurücksetzen</span>
            </button>
          </div>

          <div className="resolutions-v2-row-count">Zeilen: {rows.length} / Gesamt: {totalRows}</div>

          <div className="resolutions-v2-table-wrap">
            <table className="resolutions-v2-table resolution-overview-v2-table">
              <colgroup>
                <col style={{ width: "154px" }} />
                <col style={{ width: "126px" }} />
                <col style={{ width: "360px" }} />
                <col />
                <col style={{ width: "112px" }} />
                <col style={{ width: "172px" }} />
                <col style={{ width: "126px" }} />
                <col style={{ width: "198px" }} />
                <col style={{ width: "138px" }} />
                <col style={{ width: "92px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("beschlussnummer")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                          <path d="M11.7 3.9v2.8h2.8M6.7 9.2h5.9M6.7 12h5.9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("beschlussnummer", "Beschluss-ID")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("objektnummer")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M6.2 5.2h7.6v9.6H6.2z" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M6.2 8.4h7.6M6.2 11.6h7.6M9.4 5.2v9.6M12.6 5.2v9.6" stroke="currentColor" strokeWidth="1.4" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("objektnummer", "Objekt-Nr")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("objektname")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M4.2 7.3h11.6v8.5H4.2z" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M6.8 4.7h6.4v2.6H6.8zM6.9 10.3h2.1M10.9 10.3H13M6.9 13.2h2.1M10.9 13.2H13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("objektname", "Objektname")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("titel")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M5.2 3.8h6.5l2.9 2.9v8.6a1.2 1.2 0 0 1-1.2 1.2H5.2A1.2 1.2 0 0 1 4 15.3V5a1.2 1.2 0 0 1 1.2-1.2Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                          <path d="M6.8 9.4h6.1M6.8 12.2h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("titel", "Titel")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("status")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
                          <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("status", "Status")}
                    </button>
                  </th>
                  <th>
                    <span className="resolutions-v2-th-plain">
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <rect x="4.3" y="5.6" width="11.4" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M6.8 3.8v2.4M13.2 3.8v2.4M4.3 8.8h11.4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="resolutions-v2-th-label">Laufzeit</span>
                    </span>
                  </th>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("restlaufzeit")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="11" r="5.8" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M10 11V7.8M10 11l2.5 1.8M8 3.8h4M9 3.8v1.8M11 3.8v1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("restlaufzeit", "Restlaufzeit")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort("ruecklaufquote")}>
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M15.2 7.2a5.8 5.8 0 1 0 .4 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                          <path d="m14 4.7 2.3 2.5-2.5 2.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      {renderSortableHeaderText("ruecklaufquote", "Rücklaufquote")}
                    </button>
                  </th>
                  <th>
                    <span className="resolutions-v2-th-plain">
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <path d="M6.2 4.8h7.6v1.3a3.8 3.8 0 0 1-7.6 0V4.8Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                          <path d="M8.2 10.2h3.6M10 10.2v2.6M7.6 15h4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                          <path d="M6.2 5.6H4.6A1.8 1.8 0 0 0 2.8 7.4v.2a2.6 2.6 0 0 0 2.6 2.6h.5M13.8 5.6h1.6a1.8 1.8 0 0 1 1.8 1.8v.2a2.6 2.6 0 0 1-2.6 2.6h-.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="resolutions-v2-th-label">Ergebnis</span>
                    </span>
                  </th>
                  <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">
                    <span className="resolutions-v2-th-plain">
                      <span className="resolutions-v2-th-icon" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none">
                          <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
                          <circle cx="10" cy="10" r="1.4" fill="currentColor" />
                          <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
                        </svg>
                      </span>
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isActive = r.status === "aktiv";
                  const isCompleted = r.status === "abgeschlossen";
                  const canEdit = r.status === "entwurf";
                  const hasVotes = (r.abgegebene_stimmen ?? 0) > 0;
                  const blockedByCompletedPermission = isCompleted && !canDeleteCompletedResolutions;
                  const canDeleteCompletedWithVotes = isCompleted && canDeleteCompletedResolutions;
                  const blockedByMissingDeletePermission = !isCompleted && !canDeleteResolutions;
                  const blockedByVotes = hasVotes && !canDeleteCompletedWithVotes;
                  const outcome = getOutcome(r);
                  const resolvedObjectNumber = (r.objektnummer || objectById.get(r.objectId)?.objektnummer || "-").toString();
                  const progress = Math.max(0, Math.min(100, Number(r.ruecklaufquote ?? 0)));
                  const remainingDays = getRemainingDays(r.endedatum);
                  const menuId = r.id;
                  return (
                    <tr key={r.id}>
                      <td>
                        <Link className="badge resolution-number" to={`/resolutions/${r.id}`} title={r.beschlussnummer}>
                          {r.beschlussnummer}
                        </Link>
                      </td>
                      <td>
                        <Link className="badge object-number" to={`/objects/${r.objectId}`} title={resolvedObjectNumber} style={{ textDecoration: "none" }}>
                          {resolvedObjectNumber}
                        </Link>
                      </td>
                      <td className="resolutions-v2-object-cell">{r.objektname || objectById.get(r.objectId)?.objektname || "-"}</td>
                      <td className="resolutions-v2-title-cell">{r.titel}</td>
                      <td>
                        <span className={`badge ${r.status}`}>{formatStatus(r.status)}</span>
                      </td>
                      <td>
                        <div className="resolutions-v2-date-range">
                          <span className="resolutions-v2-date-item">
                            <span className="resolutions-v2-date-item-icon" aria-hidden="true">
                              <svg viewBox="0 0 16 16" fill="none">
                                <path d="M3.4 3.6h9.2v8.8H3.4z" stroke="currentColor" strokeWidth="1.4" />
                                <path d="M5.2 2.5v2.1M10.8 2.5v2.1M3.4 6h9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                              </svg>
                            </span>
                            <span>{formatDateDE(r.startdatum)}</span>
                          </span>
                          <span className="resolutions-v2-date-item">
                            <span className="resolutions-v2-date-item-icon" aria-hidden="true">
                              <svg viewBox="0 0 16 16" fill="none">
                                <path d="M3.4 3.6h9.2v8.8H3.4z" stroke="currentColor" strokeWidth="1.4" />
                                <path d="M5.2 2.5v2.1M10.8 2.5v2.1M3.4 6h9.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                <path d="m5.6 9 1.4 1.4 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                            <span>{formatDateDE(r.endedatum)}</span>
                          </span>
                        </div>
                      </td>
                      <td>
                        {remainingDays === null ? (
                          "-"
                        ) : (
                          <span className="resolutions-v2-remaining-bullet" title={`${remainingDays} Tage`}>
                            {remainingDays}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="resolutions-v2-progress-wrap">
                          <span>{progress.toFixed(2)} %</span>
                          <span className="resolutions-v2-progress-track" aria-hidden="true">
                            <span className="resolutions-v2-progress-fill" style={{ width: `${progress}%` }} />
                          </span>
                        </div>
                      </td>
                      <td>
                        {outcome.tone ? (
                          <span className={`badge ergebnis-${outcome.tone === "success" ? "angenommen" : "abgelehnt"}`}>{outcome.label}</span>
                        ) : (
                          <span className="badge ergebnis-neutral">{outcome.label}</span>
                        )}
                      </td>
                      <td>
                        <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="resolutions-v2-action-trigger"
                            aria-label={`Aktionen für ${r.beschlussnummer}`}
                            aria-expanded={openActionMenuId === menuId}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openActionMenuId === menuId) {
                                setOpenActionMenuId(null);
                                return;
                              }
                              openActionMenuAtTrigger(menuId, e.currentTarget);
                            }}
                          >
                            ⋮
                          </button>
                          {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                              <div
                                className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onMouseEnter={() => setIsActionMenuHovered(true)}
                                onMouseLeave={() => setIsActionMenuHovered(false)}
                              >
                              {canEditResolutions ? (
                                <button
                                  type="button"
                                  disabled={!canEdit}
                                  onClick={() => {
                                    if (!canEdit) {
                                      setActionError("Nur Entwürfe können bearbeitet werden. Laufende oder beendete Beschlüsse sind gesperrt.");
                                      setOpenActionMenuId(null);
                                      return;
                                    }
                                    setEditOwnerSearch("");
                                    setEditUnitTypeFilter("");
                                    setEditEntranceFilter("");
                                    setEditForm({
                                      id: r.id,
                                      objectId: r.objectId,
                                      beschlussnummer: r.beschlussnummer,
                                      titel: r.titel,
                                      startdatum: r.startdatum ?? "",
                                      endedatum: r.endedatum ?? "",
                                      stimmrechtsart: r.stimmrechtsart,
                                      quorum: r.quorum,
                                      vorbeschluss_top: r.vorbeschluss_top ?? "",
                                      vorbeschluss_wev_datum: r.vorbeschluss_wev_datum ?? "",
                                      beschlusstext: r.beschlusstext,
                                      facilioo_vorgangsnummer: r.facilioo_vorgangsnummer ? String(r.facilioo_vorgangsnummer) : "",
                                      dispatch_automation_enabled: r.dispatch_automation_enabled ?? false,
                                      trigger_notifications_enabled: r.trigger_notifications_enabled ?? false,
                                      trigger_notifications_channel: r.trigger_notifications_channel ?? "email",
                                      trigger_notifications_events: normalizeTriggerNotificationEvents(r.trigger_notifications_events),
                                      stimmberechtigte_einheiten: r.stimmberechtigte_einheiten ?? []
                                    });
                                    setOpenActionMenuId(null);
                                  }}
                                >
                                  Bearbeiten
                                </button>
                              ) : null}
                              {r.status === "entwurf" ? (
                                <button
                                  type="button"
                                  disabled={startMutation.isPending}
                                  onClick={() => {
                                    if (!r.endedatum) {
                                      setStartConfirm({ id: r.id, beschlussnummer: r.beschlussnummer, endedatum: "", error: "" });
                                    } else {
                                      startMutation.mutate({ id: r.id, endedatum: r.endedatum });
                                    }
                                    setOpenActionMenuId(null);
                                  }}
                                >
                                  Starten
                                </button>
                              ) : null}
                              {r.status === "aktiv" ? (
                                <button
                                  type="button"
                                  disabled={stopMutation.isPending}
                                  onClick={() => {
                                    setStopConfirm({
                                      id: r.id,
                                      beschlussnummer: r.beschlussnummer,
                                      code: String(Math.floor(1000 + Math.random() * 9000)),
                                      input: ["", "", "", ""],
                                      error: ""
                                    });
                                    setOpenActionMenuId(null);
                                  }}
                                >
                                  Stoppen
                                </button>
                              ) : null}
                              {canDeleteAnyResolution ? (
                                <button
                                  type="button"
                                  disabled={
                                    isActive ||
                                    blockedByMissingDeletePermission ||
                                    blockedByVotes ||
                                    blockedByCompletedPermission ||
                                    deleteMutation.isPending
                                  }
                                  onClick={() => {
                                    if (isActive) {
                                      setActionError("Aktive Beschlüsse können nicht gelöscht werden.");
                                      setOpenActionMenuId(null);
                                      return;
                                    }
                                    if (blockedByMissingDeletePermission) {
                                      setActionError("Keine Berechtigung zum Löschen dieses Beschlusses.");
                                      setOpenActionMenuId(null);
                                      return;
                                    }
                                    if (blockedByVotes) {
                                      setActionError("Beschlüsse mit Stimmabgaben können nicht gelöscht werden.");
                                      setOpenActionMenuId(null);
                                      return;
                                    }
                                    if (blockedByCompletedPermission) {
                                      setActionError("Keine Berechtigung zum Löschen abgeschlossener Beschlüsse.");
                                      setOpenActionMenuId(null);
                                      return;
                                    }
                                    setDeleteConfirm({
                                      id: r.id,
                                      beschlussnummer: r.beschlussnummer,
                                      code: String(Math.floor(1000 + Math.random() * 9000)),
                                      input: ["", "", "", ""],
                                      error: ""
                                    });
                                    setOpenActionMenuId(null);
                                  }}
                                >
                                  Löschen
                                </button>
                              ) : null}
                            </div>,
                            document.body
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="resolutions-v2-pagination">
            <div className="resolutions-v2-page-size">
              <select
                value={String(pageSize)}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setPageSize(Number.isFinite(next) && next > 0 ? next : 10);
                }}
              >
                <option value="10">10 pro Seite</option>
                <option value="20">20 pro Seite</option>
                <option value="50">50 pro Seite</option>
              </select>
            </div>

            <div className="resolutions-v2-page-center">
              <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page <= 1}>
                ‹
              </button>
              {pageItems.map((pageItem) => (
                <button
                  key={pageItem}
                  type="button"
                  className={`resolutions-v2-page-chip ${pageItem === page ? "active" : ""}`}
                  onClick={() => setPage(pageItem)}
                >
                  {pageItem}
                </button>
              ))}
              <button
                type="button"
                className="resolutions-v2-page-arrow"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                ›
              </button>
              <span className="resolutions-v2-page-info">Seite {page} von {totalPages}</span>
            </div>

            <div className="resolutions-v2-page-next-wrap">
              <button
                type="button"
                className="resolutions-v2-next-btn"
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={page >= totalPages}
              >
                <span>Weiter</span>
                <span aria-hidden="true">›</span>
              </button>
            </div>
          </div>
        </section>
      ) : (
        <ServerDataTable
          columns={columns}
          rows={rows}
          total={totalRows}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          bodyMaxHeight={560}
          topContent={
            <section className="filterbar" style={{ margin: 0 }}>
              <input placeholder="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ResolutionStatusFilter)}>
                <option value="">Alle Status</option>
                <option value="entwurf">Entwurf</option>
                <option value="aktiv">Läuft</option>
                <option value="abgeschlossen">Beendet</option>
              </select>
              <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value as ResolutionResultFilter)}>
                <option value="">Alle Ergebnisse</option>
                <option value="angenommen">Angenommen</option>
                <option value="abgelehnt">Abgelehnt</option>
                <option value="offen">Offen</option>
              </select>
            </section>
          }
        />
      )
    ) : null}
    </div>
  </>;
};
































